import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Download as DownloadIcon, Monitor, HardDrive, Cpu, Layers } from 'lucide-react';
type Language = 'es' | 'en' | 'pt';
const translations = {
  es: {
    title: 'Comienza Tu',
    titleHighlight: 'Aventura',
    subtitle: 'Descarga el cliente oficial de MU Vanguard para experimentar la jugabilidad definitiva de la Temporada 6. Completamente optimizado y protegido.',
    downloadClient: 'Descargar Cliente',
    fullVersion: 'Versión Completa',
    installationGuide: 'Guía de Instalación',
    systemRequirements: 'Requisitos del Sistema',
    os: 'SO',
    osDesc: 'Windows 7, 8, 10, 11 (64-bit)',
    processorRam: 'Procesador y RAM',
    processorDesc: 'Intel Core i3 / AMD Ryzen 3 o superior • 2GB+ RAM',
    storageGraphics: 'Almacenamiento y Gráficos',
    storageDesc: '5GB de Espacio Libre • Compatible con DirectX 9.0c'
  },
  en: {
    title: 'Start Your',
    titleHighlight: 'Adventure',
    subtitle: 'Download the official MU Vanguard client to experience the ultimate Season 6 gameplay. Fully optimized and protected.',
    downloadClient: 'Download Client',
    fullVersion: 'Full Version',
    installationGuide: 'Installation Guide',
    systemRequirements: 'System Requirements',
    os: 'OS',
    osDesc: 'Windows 7, 8, 10, 11 (64-bit)',
    processorRam: 'Processor & RAM',
    processorDesc: 'Intel Core i3 / AMD Ryzen 3 or better • 2GB+ RAM',
    storageGraphics: 'Storage & Graphics',
    storageDesc: '5GB Free Space • DirectX 9.0c Compatible'
  },
  pt: {
    title: 'Comece Sua',
    titleHighlight: 'Aventura',
    subtitle: 'Baixe o cliente oficial do MU Vanguard para experimentar a jogabilidade definitiva da Temporada 6. Totalmente otimizado e protegido.',
    downloadClient: 'Baixar Cliente',
    fullVersion: 'Versão Completa',
    installationGuide: 'Guia de Instalação',
    systemRequirements: 'Requisitos do Sistema',
    os: 'SO',
    osDesc: 'Windows 7, 8, 10, 11 (64-bit)',
    processorRam: 'Processador e RAM',
    processorDesc: 'Intel Core i3 / AMD Ryzen 3 ou superior • 2GB+ RAM',
    storageGraphics: 'Armazenamento e Gráficos',
    storageDesc: '5GB de Espaço Livre • Compatível com DirectX 9.0c'
  }
};
export function Download() {
  const [language, setLanguage] = useState<Language>('es');
  const t = translations[language];
  useEffect(() => {
    const savedLang = localStorage.getItem('language') as Language;
    if (savedLang) setLanguage(savedLang);
    const handleLanguageChange = (e: CustomEvent<Language>) => {
      setLanguage(e.detail);
    };
    window.addEventListener('languageChange', handleLanguageChange as EventListener);
    return () => window.removeEventListener('languageChange', handleLanguageChange as EventListener);
  }, []);
  return <section id="download" className="py-16 md:py-24 relative overflow-hidden" data-id="element-16">
      <div className="absolute inset-0 z-0" data-id="element-17">
        <div className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-20" style={{
        backgroundImage: 'url("https://cdn.magicpatterns.com/uploads/eYBYRLuoC8pAFP1V97bAhQ/ChatGPT_Image_Mar_25,_2026,_10_00_56_AM.png")',
        filter: 'grayscale(50%) blur(4px)'
      }} data-id="element-18" />
        <div className="absolute inset-0 bg-gradient-to-b from-darkerBlue via-darkerBlue/90 to-darkerBlue" data-id="element-19" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-20">
        <div className="flex flex-col lg:flex-row items-center gap-16" data-id="element-21">
          <motion.div initial={{
          opacity: 0,
          x: -50
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} className="flex-1 text-center lg:text-left" data-id="element-22">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6" data-id="element-23">
              {t.title}{' '}
              <span className="text-gradient-gold" data-id="element-24">{t.titleHighlight}</span>
            </h2>
            <p className="text-xl text-gray-300 mb-10 max-w-2xl mx-auto lg:mx-0" data-id="element-25">
              {t.subtitle}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start" data-id="element-26">
              <button className="btn-primary flex items-center justify-center gap-3 text-lg py-4 px-8 group" data-id="element-27">
                <DownloadIcon className="w-6 h-6 group-hover:-translate-y-1 transition-transform" data-id="element-28" />
                <div className="text-left" data-id="element-29">
                  <div className="leading-none" data-id="element-30">{t.downloadClient}</div>
                  <div className="text-xs font-inter font-normal text-darkerBlue/80 mt-1" data-id="element-31">
                    {t.fullVersion} • 1.2 GB
                  </div>
                </div>
              </button>

              <button className="btn-secondary flex items-center justify-center gap-3 text-lg py-4 px-8" data-id="element-32">
                {t.installationGuide}
              </button>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 50
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} className="flex-1 w-full max-w-md lg:max-w-none" data-id="element-33">
            <div className="glass-panel p-8 rounded-2xl border-t-4 border-t-gold" data-id="element-34">
              <h3 className="text-2xl font-bold mb-6 flex items-center gap-3" data-id="element-35">
                <Monitor className="text-gold" data-id="element-36" />
                {t.systemRequirements}
              </h3>

              <ul className="space-y-6" data-id="element-37">
                <li className="flex items-start gap-4" data-id="element-38">
                  <div className="p-2 rounded bg-darkBlue border border-white/5" data-id="element-39">
                    <Layers className="w-5 h-5 text-glowBlue" data-id="element-40" />
                  </div>
                  <div data-id="element-41">
                    <h4 className="font-bold text-white" data-id="element-42">{t.os}</h4>
                    <p className="text-sm text-gray-400" data-id="element-43">{t.osDesc}</p>
                  </div>
                </li>
                <li className="flex items-start gap-4" data-id="element-44">
                  <div className="p-2 rounded bg-darkBlue border border-white/5" data-id="element-45">
                    <Cpu className="w-5 h-5 text-glowBlue" data-id="element-46" />
                  </div>
                  <div data-id="element-47">
                    <h4 className="font-bold text-white" data-id="element-48">{t.processorRam}</h4>
                    <p className="text-sm text-gray-400" data-id="element-49">{t.processorDesc}</p>
                  </div>
                </li>
                <li className="flex items-start gap-4" data-id="element-50">
                  <div className="p-2 rounded bg-darkBlue border border-white/5" data-id="element-51">
                    <HardDrive className="w-5 h-5 text-glowBlue" data-id="element-52" />
                  </div>
                  <div data-id="element-53">
                    <h4 className="font-bold text-white" data-id="element-54">
                      {t.storageGraphics}
                    </h4>
                    <p className="text-sm text-gray-400" data-id="element-55">{t.storageDesc}</p>
                  </div>
                </li>
              </ul>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}